import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Analytics } from '@vercel/analytics/react'
import { ThemeProvider } from '@/components/theme-provider'
import { AuthProvider } from '@/lib/auth/auth-context'
import { TransactionProvider } from '@/lib/context/transaction-context'
import { StreakProvider } from '@/lib/streak/use-streak-state'
import { Toaster } from '@/components/ui/sonner'
import { WelcomeGuard } from '@/components/WelcomeGuard'
import { ErrorBoundary } from '@/components/error-boundary'
import { BiscuitSkeleton } from '@/components/biscuit-skeleton'
import AuthPage from '@/pages/AuthPage'
import ProtectedRoute from '@/components/ProtectedRoute'

// Lazy-load non-first-paint route pages so recharts/chart-vendor (pulled in by
// HomePage -> Dashboard) stays out of the entry chunk. /auth is the first-paint
// redirect target, so AuthPage and the app shell stay eager.
const HomePage = lazy(() => import('@/pages/HomePage'))
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const WelcomePage = lazy(() => import('@/pages/WelcomePage'))
const DevBiscuitPage = lazy(() => import('@/pages/DevBiscuitPage'))

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
          <AuthProvider>
            <StreakProvider>
              <TransactionProvider>
                <WelcomeGuard>
                  <Suspense
                    fallback={
                      <div className="flex min-h-dvh items-center justify-center p-8">
                        <BiscuitSkeleton />
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/auth" element={<AuthPage />} />
                      <Route
                        path="/home"
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary>
                              <HomePage />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      {import.meta.env.DEV && (
                        <Route path="/dev/biscuit" element={<DevBiscuitPage />} />
                      )}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </WelcomeGuard>
                <Toaster />
              </TransactionProvider>
            </StreakProvider>
          </AuthProvider>
        </ThemeProvider>
        <Analytics />
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
